import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch } from 'vue';

import RangeInput from '@/ui-components/RangeInput.vue';
import ToggleButton from '@/ui-components/ToggleButton.vue';
import ResetButton from '@/components/buttons/ResetButton.vue';

import { useMetaStore, useFilterStore, useLanguageStore } from '@/helpers/composables/useStores';
import { useI18n } from '@/helpers/composables/useI18n';
import { FilterCollection } from '@/stores/filter.store';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { WebLocale } from '@/enums/web-locale';
import { AppDirection } from '@/enums/app-direction';
import { CollectionType } from '@/enums/collection-type';

interface PricePreset {
	min_price: number;
	max_price: number | null;
	label: string | any;
	trackingKey: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'FilterPrice',
  props: {
    collectionType: null,
    currentFilters: null
  },
  setup(__props: any) {

const props = __props as {
	collectionType: CollectionType;
	currentFilters: FilterCollection;
};



const { t, tc } = useI18n();
const { webLocale } = useLanguageStore();
const { setRange, resetFilters, filters } = useFilterStore();
const { appDirection } = useMetaStore();

const FILTER_NAMES = ['min_price', 'max_price'] as const;
const PRICE_STEP = 1;
const BASE_MAX_PRICE = 50;
const PRESET_VALUES = [1, 2, 3, 5] as const;

const CURRENCY_AMPLIFIERS: Record<string, number> = {
	[WebLocale.br]: 5,
	[WebLocale.in]: 50,
	[WebLocale.jp]: 100,
	[WebLocale.kr]: 1000,
};

const currencyAmplifier = computed(() => CURRENCY_AMPLIFIERS[webLocale.value] ?? 1);

const minPrice = 0;
const maxPrice = computed(() => currencyAmplifier.value * BASE_MAX_PRICE);
const priceStep = PRICE_STEP;

const currentPriceFilter = computed(() => {
	const collectionFilters = filters.value(props.collectionType);
	return {
		min_price: collectionFilters.min_price,
		max_price: collectionFilters.max_price,
	};
});

const price = ref([props.currentFilters.min_price || minPrice, props.currentFilters.max_price || maxPrice.value]);

const presets = computed<PricePreset[]>(() => {
	const amp = currencyAmplifier.value;

	const result: PricePreset[] = PRESET_VALUES.map((multiplier): PricePreset => {
		const value = amp * multiplier;
		return {
			min_price: 0,
			max_price: value as number | null,
			label: t('WEBAPP_PRICEFILTER_UPTO', { x: value }) as string,
			trackingKey: `upto-${value}`,
		};
	});

	const lastValue = amp * 5;
	result.push({
		min_price: lastValue,
		max_price: null as number | null,
		label: t('WEBAPP_PRICEFILTER_MORETHAN', { x: lastValue }) as string,
		trackingKey: `morethan-${lastValue}`,
	});

	return result;
});

const formattedMinPrice = computed(() => formatPrice(minPrice));
const formattedMaxPrice = computed(() => formatPrice(maxPrice.value));

function setPreset(preset: PricePreset) {
	const isActive = isPriceFilterActive(preset.min_price, preset.max_price);

	if (isActive) {
		resetFilters({ filterNames: FILTER_NAMES });
		TrackingHelper.trackEvent('userinteraction', {
			action: 'filter_toggled',
			label: 'price',
			property: preset.trackingKey,
			value: 0,
		});
	} else {
		setRange({
			rangeName: 'price',
			trackingKey: preset.trackingKey,
			upper: preset.max_price,
			lower: preset.min_price,
			selectedState: 1,
		});
	}
}

function isPriceFilterActive(min: number, max: number | null): boolean {
	const { min_price, max_price } = currentPriceFilter.value;
	return min === min_price && max === max_price;
}

function formatPrice(priceValue: number): string {
	const currency = tc('WEBAPP_STANDARD_CURRENCY');
	return appDirection.value === AppDirection.RTL ? `${priceValue}${currency}` : `${currency}${priceValue}`;
}

// Track if we're updating from the store to prevent circular updates
let isUpdatingFromStore = false;

// Watch store changes and update price slider
watch(
	currentPriceFilter,
	({ min_price, max_price }) => {
		const newMin = min_price ?? minPrice;
		const newMax = max_price ?? maxPrice.value;

		if (newMin !== price.value[0] || newMax !== price.value[1]) {
			isUpdatingFromStore = true;
			price.value = [newMin, newMax];
		}
	},
	{ immediate: true }
);

// Watch price slider changes and update store
watch(price, newPrice => {
	// Skip if update came from store
	if (isUpdatingFromStore) {
		isUpdatingFromStore = false;
		return;
	}

	const [lower, upper] = newPrice;
	const max = maxPrice.value;

	setRange({
		rangeName: 'price',
		trackingKey: 'knob',
		upper: upper === max ? null : upper,
		lower: lower === minPrice ? null : lower,
	});
});

return { __sfc: true,props, t, tc, webLocale, setRange, resetFilters, filters, appDirection, FILTER_NAMES, PRICE_STEP, BASE_MAX_PRICE, PRESET_VALUES, CURRENCY_AMPLIFIERS, currencyAmplifier, minPrice, maxPrice, priceStep, currentPriceFilter, price, presets, formattedMinPrice, formattedMaxPrice, setPreset, isPriceFilterActive, formatPrice, isUpdatingFromStore, RangeInput, ToggleButton, ResetButton }
}

})